import { CSSProperties, useMemo } from 'react'

import { Anchor, Badge, Box, Group, Paper, Stack, Text, Title } from '@mantine/core'
import { useQueries } from '@tanstack/react-query'
import { useGetList, useTranslate } from 'react-admin'
import {
  EndscreenConfigApiDTO,
  EndscreenElementConfigApiDTO,
  InfocardConfigApiDTO,
  InfocardsConfigApiDTO,
} from '../../codegen/generated/Api.schemas'
import {
  getApiVideoPromotionVideos,
  getGetApiVideoPromotionVideosQueryKey,
  useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists,
} from '../../codegen/generated/video-promotion'
import { YoutubeChannel } from '../../types/YoutubeChannel'

type PromotionConfigRecapProps = {
  youtubeChannelId: string
  endscreen?: EndscreenConfigApiDTO
  infocards?: InfocardsConfigApiDTO
}

const formatTimecode = (seconds: number) =>
  [Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]
    .map(part => part.toString().padStart(2, '0'))
    .join(':')

const useTargetNames = (
  youtubeChannelId: string,
  endscreen?: EndscreenConfigApiDTO,
  infocards?: InfocardsConfigApiDTO,
) => {
  const videoIds = useMemo(() => {
    const ids = new Set<string>()
    if (endscreen?.mode === 'custom') {
      for (const element of endscreen.elements) {
        if (element.type === 'video' && element.videoId) {
          ids.add(element.videoId)
        }
      }
    }
    if (infocards?.mode === 'custom') {
      for (const card of infocards.cards) {
        if (card.type === 'video') {
          ids.add(card.videoId)
        }
      }
    }
    return Array.from(ids)
  }, [endscreen, infocards])

  const videoQueries = useQueries({
    queries: videoIds.map(videoId => {
      const params = { youtubeChannelId, videoId, pageSize: 1 }
      return {
        queryKey: getGetApiVideoPromotionVideosQueryKey(params),
        queryFn: () => getApiVideoPromotionVideos(params),
      }
    }),
  })
  const playlistsQuery = useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists(youtubeChannelId)
  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  })

  return {
    video: (id: string) =>
      videoQueries.flatMap(query => query.data?.body.videos ?? []).find(v => v.id === id)?.title ??
      id,
    playlist: (id: string) =>
      playlistsQuery.data?.body.playlists.find(p => p.id === id)?.title ?? id,
    channel: (id: string) => youtubeChannelsQuery.data?.find(c => c.id === id)?.name ?? id,
  }
}

const YoutubeVideoLink = ({ videoId }: { videoId: string }) => (
  <Anchor href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noreferrer">
    {videoId}
  </Anchor>
)

export const PromotionConfigRecap = ({
  youtubeChannelId,
  endscreen,
  infocards,
}: PromotionConfigRecapProps) => {
  const t = useTranslate()
  const names = useTargetNames(youtubeChannelId, endscreen, infocards)

  const elementLabel = (element: EndscreenElementConfigApiDTO) => {
    switch (element.type) {
      case 'video':
        if (element.videoType === 'RECENT_UPLOAD') {
          return { type: t('pages.videoPromotion.endScreen.slotTypes.recentUpload') }
        }
        if (element.videoType === 'BEST_FOR_VIEWER') {
          return { type: t('pages.videoPromotion.endScreen.slotTypes.bestForViewer') }
        }
        return {
          type: t('pages.videoPromotion.endScreen.slotTypes.videoFixed'),
          target: element.videoId ? names.video(element.videoId) : undefined,
        }
      case 'playlist':
        return {
          type: t('pages.videoPromotion.endScreen.slotTypes.playlist'),
          target: names.playlist(element.playlistId),
        }
      case 'channel':
        return {
          type: t('pages.videoPromotion.endScreen.slotTypes.channel'),
          target: names.channel(element.channelId),
        }
      case 'subscribe':
        return { type: t('pages.videoPromotion.endScreen.slotTypes.subscribe') }
    }
  }

  const cardTarget = (card: InfocardConfigApiDTO) => {
    switch (card.type) {
      case 'video':
        return names.video(card.videoId)
      case 'playlist':
        return names.playlist(card.playlistId)
      case 'channel':
        return names.channel(card.channelId)
      case 'link':
        return card.title || card.targetUrl
    }
  }

  return (
    <Stack gap="lg">
      {!!endscreen && (
        <Stack gap="xs">
          <Title order={6}>{t('pages.videoPromotion.recap.endscreenTitle')}</Title>
          {endscreen.mode === 'copyFromVideo' ? (
            <Text size="sm">
              {t('pages.videoPromotion.recap.copiedFrom')}{' '}
              <YoutubeVideoLink videoId={endscreen.sourceVideoId} />
            </Text>
          ) : (
            <>
              <Box
                pos="relative"
                w="100%"
                maw={560}
                bg="dark.6"
                bdrs="sm"
                style={{ aspectRatio: '16 / 9', overflow: 'hidden' }}
              >
                {endscreen.elements.map(element => {
                  const { type, target } = elementLabel(element)
                  const isRound = element.type === 'subscribe' || element.type === 'channel'
                  const style: CSSProperties = {
                    position: 'absolute',
                    left: `${element.position.left * 100}%`,
                    top: `${element.position.top * 100}%`,
                    width: `${element.position.width * 100}%`,
                    aspectRatio: isRound ? '1 / 1' : '16 / 9',
                    borderRadius: isRound ? '50%' : 'var(--mantine-radius-sm)',
                  }
                  return (
                    <Stack
                      key={`${element.position.left}-${element.position.top}`}
                      style={style}
                      gap={2}
                      justify="center"
                      align="center"
                      p={4}
                      bg={isRound ? 'lime.1' : 'cyan.1'}
                    >
                      <Text size="xs" fw={700} ta="center" lineClamp={1}>
                        {type}
                      </Text>
                      {!!target && (
                        <Text size="xs" ta="center" lineClamp={2}>
                          {target}
                        </Text>
                      )}
                    </Stack>
                  )
                })}
              </Box>
              <Text size="sm" c="dimmed">
                {endscreen.durationSeconds
                  ? t('pages.videoPromotion.recap.timingWithDuration', {
                      start: endscreen.startSecondsBeforeEnd,
                      duration: endscreen.durationSeconds,
                    })
                  : t('pages.videoPromotion.recap.timing', {
                      start: endscreen.startSecondsBeforeEnd,
                    })}
              </Text>
            </>
          )}
        </Stack>
      )}
      {!!infocards && (
        <Stack gap="xs">
          <Title order={6}>{t('pages.videoPromotion.infoCards._title')}</Title>
          {infocards.mode === 'copyFromVideo' ? (
            <Text size="sm">
              {t('pages.videoPromotion.recap.copiedFrom')}{' '}
              <YoutubeVideoLink videoId={infocards.sourceVideoId} />
            </Text>
          ) : (
            infocards.cards.map((card, index) => (
              <Paper
                // biome-ignore lint/suspicious/noArrayIndexKey: cards have no id and keep their order
                key={index}
                withBorder
                p="sm"
                maw={560}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={4} style={{ minWidth: 0 }}>
                    <Group gap="xs" wrap="nowrap">
                      <Badge variant="light" color="violet" size="sm">
                        {t(`pages.videoPromotion.infoCards.types.${card.type}`)}
                      </Badge>
                      <Text size="sm" fw={600} truncate>
                        {cardTarget(card)}
                      </Text>
                    </Group>
                    {!!card.teaserText && (
                      <Text size="sm">
                        {t('pages.videoPromotion.infoCards.teaserText')} : {card.teaserText}
                      </Text>
                    )}
                    {!!card.customMessage && (
                      <Text size="sm">
                        {t('pages.videoPromotion.infoCards.customMessage')} : {card.customMessage}
                      </Text>
                    )}
                  </Stack>
                  <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {card.teaserStartOffsetSeconds === undefined
                      ? t('pages.videoPromotion.recap.teaserAuto')
                      : formatTimecode(card.teaserStartOffsetSeconds)}
                  </Text>
                </Group>
              </Paper>
            ))
          )}
        </Stack>
      )}
    </Stack>
  )
}
